import { useLazyQuery } from "@apollo/client/react";
import { useCallback } from "react";
import type { ResultOf } from "gql.tada";
import { useStore } from "@/app/store";
import { OP_DATA_CATALOG } from "../graphql/queries";
import {
  type CoachTypeAggregate,
  type OpDataField,
  opDataVariableLabel,
} from "./useOpDataCatalog.constants";

export interface OpDataVariableOption {
  code: string;
  displayName: string;
  opDataField: OpDataField;
  coachTypeAggregate: CoachTypeAggregate;
}

type OpDataCatalogData = ResultOf<typeof OP_DATA_CATALOG>;

const toCatalog = (
  raw: OpDataCatalogData["opDataCatalog"] | undefined,
): OpDataVariableOption[] =>
  (raw ?? [])
    .map((entry) => ({
      code: entry.code,
      displayName: opDataVariableLabel(
        entry.opDataField,
        entry.coachTypeAggregate,
      ),
      opDataField: entry.opDataField,
      coachTypeAggregate: entry.coachTypeAggregate,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

const useOpDataCatalog = () => {
  const catalog = useStore((s) => s.opDataCatalog);
  const error = useStore((s) => s.opDataCatalogError);

  return {
    opDataVariables: catalog ?? [],
    loading: catalog === null && error === null,
    error,
  };
};

export const useOpDataCatalogPrefetch = () => {
  const [fetchOpDataCatalog] = useLazyQuery(OP_DATA_CATALOG);

  return useCallback(() => {
    if (useStore.getState().opDataCatalog !== null) return;

    const storeError = (e: unknown) =>
      useStore
        .getState()
        .setOpDataCatalogError(
          e instanceof Error ? e : new Error("Failed to load Operational Data"),
        );

    void fetchOpDataCatalog()
      .then((res) => {
        if (res.error) {
          storeError(res.error);
          return;
        }
        useStore
          .getState()
          .setOpDataCatalog(toCatalog(res.data?.opDataCatalog));
      })
      .catch(storeError);
  }, [fetchOpDataCatalog]);
};

export default useOpDataCatalog;
